import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Alert,
    Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';

export default function EditGroupDialog({ open, group, onClose, onSave, onDelete }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [urls, setUrls] = useState([]);
    const [newUrl, setNewUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (group) {
            setName(group.name || '');
            setDescription(group.description || '');
            setUrls(group.urls || []);
        }
    }, [group]);

    const handleAddUrl = () => {
        if (newUrl.trim() && !urls.includes(newUrl.trim())) {
            setUrls(prev => [...prev, newUrl.trim()]);
            setNewUrl('');
        }
    };

    const handleRemoveUrl = (index) => {
        setUrls(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!name.trim()) return;
        if (urls.length === 0) return;

        setLoading(true);
        try {
            await onSave(group.idProxyGroup, {
                name: name.trim(),
                description: description.trim(),
                urls: urls
            });
            onClose();
        } catch (error) {
            console.error('Errore nel salvataggio:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!group || !onDelete) return;
        
        setDeleting(true);
        try {
            await onDelete(group.idProxyGroup);
            onClose();
        } catch (error) {
            console.error('Errore nell\'eliminazione del gruppo:', error);
        } finally {
            setDeleting(false);
        }
    };

    const handleClose = () => {
        setName('');
        setDescription('');
        setUrls([]);
        setNewUrl('');
        setShowDeleteConfirm(false);
        onClose();
    };

    if (!group) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Modifica Gruppo: {group.name}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Nome del gruppo"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        required
                    />

                    <TextField
                        label="Descrizione"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                    />

                    <Typography variant="h6" sx={{ mt: 2 }}>
                        URL del gruppo ({urls.length})
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            label="Nuovo URL"
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="example.com"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddUrl();
                                }
                            }}
                        />
                        <Button
                            variant="outlined"
                            onClick={handleAddUrl}
                            disabled={!newUrl.trim()}
                            startIcon={<AddIcon />}
                        >
                            Aggiungi
                        </Button>
                    </Box>

                    <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        {urls.map((url, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={url} />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleRemoveUrl(index)}
                                        size="small"
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>

                    {urls.length === 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                            Aggiungi almeno un URL per completare il gruppo
                        </Typography>
                    )}

                    {showDeleteConfirm && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Alert severity="error" icon={<WarningIcon />}>
                                <Typography variant="body2" gutterBottom>
                                    <strong>Attenzione!</strong> Stai per eliminare definitivamente il gruppo "{group.name}".
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Questa operazione rimuoverà il gruppo dall'inventario globale e da tutte le whitelist che lo utilizzano.
                                </Typography>
                                <Typography variant="body2">
                                    Questa azione non può essere annullata.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleDelete}
                                        disabled={deleting}
                                        size="small"
                                    >
                                        {deleting ? 'Eliminando...' : 'CONFERMA ELIMINAZIONE'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowDeleteConfirm(false)}
                                        disabled={deleting}
                                        size="small"
                                    >
                                        Annulla
                                    </Button>
                                </Box>
                            </Alert>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    color="error"
                    disabled={loading || deleting || showDeleteConfirm}
                    startIcon={<DeleteIcon />}
                >
                    Elimina Gruppo
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={handleClose} disabled={loading || deleting}>
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading || deleting || !name.trim() || urls.length === 0 || showDeleteConfirm}
                    >
                        {loading ? 'Salvando...' : 'Salva Modifiche'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}